// v-dialog="closeFn" — makes an existing modal panel behave like a dialog for
// keyboard and screen-reader users without changing its markup or styling:
//
//   - role="dialog" + aria-modal, labelled by the panel's first heading
//   - focus moves into the panel when it opens and goes back to whatever had
//     it (usually the button that opened it) when it closes
//   - Tab / Shift+Tab stay inside the panel
//   - Esc calls closeFn — the same function the backdrop click calls, so a
//     modal that refuses to close on backdrop also refuses on Esc. Pass
//     nothing (v-dialog) for a panel that must not be dismissed.
//
// Dialogs can stack (the task drawer opens a log modal on top of itself), so
// only the most recently opened one reacts to keys.

const stack = [];
let uid = 0;

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(el) {
  return Array.from(el.querySelectorAll(FOCUSABLE))
    .filter(n => n.offsetParent !== null || n === document.activeElement);
}

function onKeydown(e) {
  const top = stack[stack.length - 1];
  if (!top) return;

  if (e.key === 'Escape') {
    // A dropdown inside the dialog (BaseSelect) closes itself on Esc and marks
    // the event handled; that press should not also close the dialog.
    if (e.defaultPrevented || typeof top.close !== 'function') return;
    e.preventDefault();
    top.close();
    return;
  }

  if (e.key !== 'Tab') return;
  const active = document.activeElement;
  // Focus outside the panel but not lost to <body> means a popup that belongs
  // to the dialog but is teleported out of it (BaseSelect's menu). It manages
  // its own Tab; leave it alone.
  if (!top.el.contains(active) && active && active !== document.body) return;

  const items = focusables(top.el);
  if (items.length === 0) {
    e.preventDefault();
    top.el.focus({ preventScroll: true });
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && (active === first || !top.el.contains(active))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !top.el.contains(active))) {
    e.preventDefault();
    first.focus();
  }
}

export default {
  mounted(el, binding) {
    if (!el.hasAttribute('role')) el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    if (!el.hasAttribute('aria-labelledby') && !el.hasAttribute('aria-label')) {
      const heading = el.querySelector('h1, h2, h3');
      if (heading) {
        if (!heading.id) heading.id = `dialog-title-${++uid}`;
        el.setAttribute('aria-labelledby', heading.id);
      }
    }

    const entry = { el, close: binding.value, returnTo: document.activeElement };
    el.__dialog = entry;
    if (stack.length === 0) document.addEventListener('keydown', onKeydown);
    stack.push(entry);

    // Wait a frame so v-if content and transitions have rendered.
    requestAnimationFrame(() => {
      if (!el.isConnected || el.contains(document.activeElement)) return;
      const target = el.querySelector('[autofocus]') || el;
      target.focus({ preventScroll: true });
    });
  },

  updated(el, binding) {
    if (el.__dialog) el.__dialog.close = binding.value;
  },

  unmounted(el) {
    const entry = el.__dialog;
    if (!entry) return;
    const i = stack.indexOf(entry);
    if (i !== -1) stack.splice(i, 1);
    if (stack.length === 0) document.removeEventListener('keydown', onKeydown);
    const back = entry.returnTo;
    if (back && back.isConnected && typeof back.focus === 'function') {
      back.focus({ preventScroll: true });
    }
    delete el.__dialog;
  },
};
